import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const mymilitary = process.env.REACT_APP_LINK_MYMILITARY;
const mymilitarylifestyle = process.env.REACT_APP_LINK_MYMILITARYLIFESTYLE;
const circleone = process.env.REACT_APP_LINK_CIRCLEONE;
const Base_Url = process.env.REACT_APP_API_BASE_URL;
const Membership = process.env.REACT_APP_LINK_SIGNUP

const Header = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [acctMenuOpen, setAcctMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  // Categories and search states
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState({ brands: [], categories: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCategoriesPanelOpen, setIsCategoriesPanelOpen] = useState(false);
  const [openSubcategoryIndexes, setOpenSubcategoryIndexes] = useState({});

  const acctRef = useRef(null);
  const suggestionRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(''); // Preserve search input across navigation
  const justNavigatedRef = useRef(false); // Flag to prevent suggestions after navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the home page or product page (for header search visibility)
  const isHomePage = location.pathname === '/';
  const isProductPage = location.pathname === '/product' || location.pathname.startsWith('/product/');
  // Show search section only on home page or when navigated to product page via search
  const showSearchSection = isHomePage || (isProductPage && location.state?.fromHeaderSearch === true);

  // ----- AES-GCM helpers -----
  const getAesKey = async () => {
    const raw = new TextEncoder().encode(process.env.REACT_APP_ENCRYPTION_AES_KEY);
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  };

  const b64urlToAb = (str) => {
    let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  };

  const decryptAesFromB64url = async (b64url) => {
    const packed = new Uint8Array(b64urlToAb(b64url));
    const iv = packed.slice(0, 12);
    const ct = packed.slice(12);
    const key = await getAesKey();
    const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return new TextDecoder().decode(plainBuf);
  };
  // ----------------------------

  // Read ?key=..., decrypt, store, fetch profile
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const encKey = params.get('key');
        if (!encKey) return;

        const json = await decryptAesFromB64url(encKey);
        const data = JSON.parse(json); // { uid, tk, ts }

        if (data?.uid) localStorage.setItem('userid', JSON.stringify(data.uid));
        if (data?.tk) localStorage.setItem('authToken', data.tk);

        if (data?.tk) {
          const res = await axios.get(`${Base_Url}/membership/users/profile`, {
            headers: { Authorization: `Bearer ${data.tk}` },
          });
          if (res.data.status === 'success') {
            localStorage.setItem('userProfile', JSON.stringify(res.data.data));
            setProfileName(res.data.data.name || '');
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(!!localStorage.getItem('userid'));
          }
        } else {
          setIsLoggedIn(!!localStorage.getItem('userid'));
        }

        // Clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete('key');
        window.history.replaceState({}, document.title, url.toString());
      } catch (err) {
        console.error('Failed to decrypt or fetch profile:', err);
      }
    })();
  }, []);

  // Initialize from localStorage on mount & keep in sync across tabs
  useEffect(() => {
    const initFromStorage = () => {
      const uid = localStorage.getItem('userid');
      // Check if userid exists and is valid
      let isValidUser = false;
      if (uid) {
        try {
          const parsed = JSON.parse(uid);
          isValidUser = !!parsed;
        } catch {
          isValidUser = !!uid;
        }
      }
      setIsLoggedIn(isValidUser);

      if (!profileName) {
        const cached = localStorage.getItem('userProfile');
        if (cached) {
          try {
            const p = JSON.parse(cached);
            if (p?.name) setProfileName(p.name);
          } catch { }
        }
      }
    };

    initFromStorage();

    const onStorage = (e) => {
      if (e.key === 'userid') setIsLoggedIn(!!e.newValue);
      if (e.key === 'userProfile' && e.newValue) {
        try {
          const p = JSON.parse(e.newValue);
          if (p?.name) setProfileName(p.name);
        } catch { }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [profileName]);

  // account menu: close on outside click or ESC
  useEffect(() => {
    const onDocClick = (e) => {
      if (!acctRef.current) return;
      if (!acctRef.current.contains(e.target)) {
        setAcctMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAcctMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);
  const toggleAccountMenu = () => setAcctMenuOpen((s) => !s);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Restore search input from location state or ref after navigation
  useEffect(() => {
    if (location.state?.searchTerm && location.state.searchTerm.trim().length >= 2) {
      // Restore from location state if available
      if (searchInput !== location.state.searchTerm) {
        justNavigatedRef.current = true; // Set flag to prevent auto-fetching suggestions
        setSearchInput(location.state.searchTerm);
        searchInputRef.current = location.state.searchTerm;
        // Ensure suggestions are hidden after navigation
        setShowSuggestions(false);
        setSearchSuggestions({ brands: [], categories: [] });
        // Clear the flag after a short delay
        setTimeout(() => {
          justNavigatedRef.current = false;
        }, 500);
      }
    } else if (searchInputRef.current && searchInput === '') {
      // Restore from ref if input was cleared but we have a stored value
      justNavigatedRef.current = true; // Set flag to prevent auto-fetching suggestions
      setSearchInput(searchInputRef.current);
      // Ensure suggestions are hidden after navigation
      setShowSuggestions(false);
      setSearchSuggestions({ brands: [], categories: [] });
      // Clear the flag after a short delay
      setTimeout(() => {
        justNavigatedRef.current = false;
      }, 500);
    }
  }, [location.state?.searchTerm, location.key]);

  // Handle search input changes with debouncing
  useEffect(() => {
    // Update ref when search input changes
    if (searchInput) {
      searchInputRef.current = searchInput;
    }

    // Don't fetch suggestions if we just navigated (to prevent showing suggestions after submit)
    if (justNavigatedRef.current) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchInput.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(searchInput);
      }, 300);
    } else {
      setSearchSuggestions({ brands: [], categories: [] });
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Handle click outside suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        // Don't close if clicking on the search input
        if (!event.target.classList.contains('search_frmcntrl')) {
          setShowSuggestions(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync panel state with DOM classes and handle body overlay
  useEffect(() => {
    const panel = document.querySelector('.tp-filter-offcanvas-area');
    const overlay = document.querySelector('.body-overlay');

    if (!panel) return;

    let isUpdatingFromReact = false;

    // Apply React state to DOM
    const currentState = panel.classList.contains('offcanvas-opened');
    if (currentState !== isCategoriesPanelOpen) {
      isUpdatingFromReact = true;
      if (isCategoriesPanelOpen) {
        panel.classList.add('offcanvas-opened');
        if (overlay) overlay.classList.add('opened');
      } else {
        panel.classList.remove('offcanvas-opened');
        if (overlay) overlay.classList.remove('opened');
      }
    }

    // Watch for class changes on the panel (from jQuery or other sources)
    const observer = new MutationObserver((mutations) => {
      // Skip if we just updated from React to avoid loops
      if (isUpdatingFromReact) {
        isUpdatingFromReact = false;
        return;
      }

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isOpen = panel.classList.contains('offcanvas-opened');
          if (isOpen !== isCategoriesPanelOpen) {
            setIsCategoriesPanelOpen(isOpen);
          }
        }
      });
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['class'] });

    // Handle body overlay click
    const handleOverlayClick = (e) => {
      if (e.target === overlay && isCategoriesPanelOpen) {
        setIsCategoriesPanelOpen(false);
      }
    };

    if (overlay) {
      overlay.addEventListener('click', handleOverlayClick);
    }

    return () => {
      observer.disconnect();
      if (overlay) overlay.removeEventListener('click', handleOverlayClick);
    };
  }, [isCategoriesPanelOpen]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Logout only from MMI (somo-mmi-shopping)
  const handleLogoutMMIOnly = () => {
    try {
      // Clear local storage only for this app
      localStorage.removeItem('userid');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userProfile');
      setProfileName('');
      setIsLoggedIn(false);
      setAcctMenuOpen(false);
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  // Logout from all connected sites (both apps)
  const handleLogoutAllSites = () => {
    try {
      // Clear local storage
      localStorage.removeItem('userid');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userProfile');
      setProfileName('');
      setIsLoggedIn(false);
      setAcctMenuOpen(false);

      // Navigate directly to logout URL - this is the most reliable approach
      // especially for macOS Safari which blocks iframes and popups
      const membershipUrl = process.env.REACT_APP_LINK_SIGNUP || 'http://localhost:3002';
      const logoutUrl = `${membershipUrl}/cross-app-logout`;
      
      // Get current URL to redirect back after logout
      const currentUrl = window.location.origin + window.location.pathname;
      const redirectUrl = `${logoutUrl}?redirect=${encodeURIComponent(currentUrl)}`;
      
      // Navigate directly to logout URL
      // CrossAppLogout component will handle the logout and redirect back
      console.log('Navigating to logout URL:', redirectUrl);
      window.location.href = redirectUrl;
      
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  // Fetch categories for side panel
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${Base_Url}/shopping/categories/list`);
      if (response.data.status === 'success') {
        setCategories(response.data.result || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch search suggestions
  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchSuggestions({ brands: [], categories: [] });
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(`${Base_Url}/shopping/products/suggestions?q=${encodeURIComponent(query.trim())}`);

      if (response.data.status === 'success' && response.data.result) {
        const hasSuggestions =
          (response.data.result.brands && response.data.result.brands.length > 0) ||
          (response.data.result.categories && response.data.result.categories.length > 0);

        if (hasSuggestions) {
          setSearchSuggestions({
            brands: response.data.result.brands || [],
            categories: response.data.result.categories || [],
          });
          setShowSuggestions(true);
        } else {
          setSearchSuggestions({ brands: [], categories: [] });
          setShowSuggestions(false);
        }
      } else {
        setSearchSuggestions({ brands: [], categories: [] });
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSearchSuggestions({ brands: [], categories: [] });
      setShowSuggestions(false);
    }
  };

  // Handle category click
  const handleCategoryClick = (categoryId) => {
    navigate('/product', { state: { filterLabel: 'Categories', categoryId } });
    closeCategoriesPanel();
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim().length >= 2) {
      const searchTerm = searchInput.trim();
      // Store the search term in ref to preserve it
      searchInputRef.current = searchTerm;
      // Clear suggestions immediately before navigation
      setShowSuggestions(false);
      setSearchSuggestions({ brands: [], categories: [] });
      // Clear any pending suggestion fetch
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      // If already on product page, use replace to force update
      if (isProductPage) {
        navigate('/product', { 
          state: { 
            filterLabel: 'Categories', 
            searchTerm: searchTerm, 
            fromHeaderSearch: true,
            timestamp: Date.now() // Add timestamp to force state update
          },
          replace: false // Use replace: false to ensure location change is detected
        });
      } else {
        navigate('/product', { 
          state: { 
            filterLabel: 'Categories', 
            searchTerm: searchTerm, 
            fromHeaderSearch: true 
          } 
        });
      }
      // Keep the search input text - don't clear it
      // The value is preserved in searchInputRef.current and will be restored if needed
    }
  };

  // Handle suggestion item click
  const handleSuggestionClick = (type, item) => {
    setShowSuggestions(false);
    setSearchInput('');
    if (type === 'brand') {
      navigate(`/product/${item.id}`, { state: { filterLabel: 'Brands' } });
    } else if (type === 'category') {
      // If already on product page, add timestamp to force state update
      if (isProductPage) {
        navigate('/product', { 
          state: { 
            filterLabel: 'Categories', 
            categoryId: item.id,
            timestamp: Date.now() // Add timestamp to force state update
          },
          replace: false
        });
      } else {
        navigate('/product', { state: { filterLabel: 'Categories', categoryId: item.id } });
      }
    }
  };

  // Toggle categories panel
  const toggleCategoriesPanel = () => {
    setIsCategoriesPanelOpen(prev => !prev);
  };

  // Close categories panel
  const closeCategoriesPanel = () => {
    setIsCategoriesPanelOpen(false);
  };

  // Render category tree recursively
  const renderCategoryTree = (categories, level = 0, parentIndex = '') => {
    if (!categories || categories.length === 0) return null;

    return (
      <ul className={`filter-items d-flex flex-wrap ${level > 0 ? 'ml-20 mt-10' : ''}`}>
        {categories.map((category, index) => {
          const hasChildren = category.children && category.children.length > 0;
          const categoryKey = `${parentIndex}${index}`;
          const isOpen = openSubcategoryIndexes[category.id || category._id];
          const dropdownClass = level === 0 ? `catdrpdwn${index}` : level === 1 ? `subcatdrpdwn${index}` : '';
          const subListClass = level === 0 ? `subcategorylist${index}` : level === 1 ? `childcategorylist${index}` : '';

          return (
            <li key={category.id || category._id || index} className="filter-item">
              {hasChildren ? (
                <>
                  <div
                    className={`w-100 d-inline-flex p-relative ${dropdownClass} crsr_pntr`}
                    onClick={() => {
                      setOpenSubcategoryIndexes(prev => ({
                        ...prev,
                        [category.id || category._id]: !prev[category.id || category._id],
                      }));
                    }}
                  >
                    <div className="tp-shop-widget-brand-item">
                      {category.name}
                    </div>
                    <div className="cat_drpdwn">
                      <i className={`fa-solid fa-angle-down ${isOpen ? 'rotated' : ''}`} />
                    </div>
                  </div>
                  <div
                    className={subListClass}
                    style={{
                      display: isOpen ? 'block' : 'none',
                    }}
                  >
                    {renderCategoryTree(category.children, level + 1, `${categoryKey}-`)}
                  </div>
                </>
              ) : (
                <div
                  className="crsr_pntr"
                  onClick={() => {
                    handleCategoryClick(category.id || category._id);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="tp-shop-widget-brand-item">
                    {category.name}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <header>
        {showBackToTop && (
          <div className="back_to_top_wrapper">
            <button type="button" className="back_to_top_btn" onClick={scrollToTop}>
              <i className="fa-solid fa-angle-up" />
            </button>
          </div>
        )}

        {/* Sidebar Overlay */}
        <div className={`offcanvas__area ${isSidebarOpen ? 'offcanvas-opened' : ''}`}>
          <div className="offcanvas__wrapper">
            <div className="offcanvas__close">
              <button className="offcanvas__close-btn" onClick={toggleSidebar}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="offcanvas__content">
              <div className="offcanvas__top mb-70 d-flex justify-content-between align-items-center">
                <div className="offcanvas__logo logo">
                  <a href="/"><img src="/assets/img/logo/logo.png" alt="logo" /></a>
                </div>
              </div>
              <div className="tp-main-menu-mobile fix mb-40">
                <nav className="tp-main-menu-content">
                  <ul>
                    <li><Link to="/" className="active">Insider</Link></li>
                    <li className={`has-dropdown ${mobileDropdownOpen ? 'dropdown-opened' : ''}`}>
                      <a onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)} style={{ cursor: 'pointer' }}>
                        MyMilitary Brands
                        <button
                          className={`dropdown-toggle-btn ${mobileDropdownOpen ? 'dropdown-opened' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileDropdownOpen(!mobileDropdownOpen);
                          }}
                        >
                          <i className="fa-solid fa-caret-right"></i>
                        </button>
                      </a>
                      <ul
                        className="tp-submenu"
                        style={{
                          display: mobileDropdownOpen ? 'block' : 'none',
                          paddingLeft: '20px',
                          marginTop: '10px'
                        }}
                      >
                        <li><a href={mymilitary} target="_blank" rel="noopener noreferrer">MyMilitarySavings</a></li>
                        <li><a href={mymilitarylifestyle} target="_blank" rel="noopener noreferrer">MyMilitaryLifestyle</a></li>
                        <li><a href={circleone} target="_blank" rel="noopener noreferrer">CircleOne</a></li>
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
        <div className="tp-header-area tp-header-style-primary tp-header-height">
          {/* header top start  */}
          <div className="tp-header-top theme-bg p-relative z-index-1">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-12">
                  <div className="tp-header-top-right d-flex align-items-center justify-content-end">
                    <div className="tp-header-top-menu d-flex align-items-center justify-content-end">
                      <div
                        className={`tp-header-top-menu-item tp-header-setting f_white ${acctMenuOpen ? 'is-open' : ''}`}
                        ref={acctRef}
                        style={{ position: 'relative' }}
                      >
                        {isLoggedIn ? (
                          <>
                            <button
                              type="button"
                              className="tp-header-setting-toggle"
                              aria-haspopup="true"
                              aria-expanded={acctMenuOpen}
                              aria-controls="account-menu"
                              onClick={toggleAccountMenu}
                            >
                              <i className="fa-regular fa-circle-user f_sz16 mr-5" />
                              {profileName || 'Account'}
                              <i className="fa-solid fa-chevron-down ml-5" aria-hidden="true" />
                            </button>

                            {/* DROPDOWN MENU — visible only when acctMenuOpen */}
                            <div
                              id="account-menu"
                              className={`dropdown-menu dropdown-menu-right ${acctMenuOpen ? 'show' : ''}`}
                              role="menu"
                            >

                              <a
                                role="menuitem"
                                className="dropdown-item"
                                href={`${Membership}/profile`}
                                onClick={() => setAcctMenuOpen(false)}
                              >
                                My Profile
                              </a>
                              <a
                                role="menuitem"
                                className="dropdown-item"
                                href={`${Membership}/invoice`}
                                onClick={() => setAcctMenuOpen(false)}
                              >
                                My Membership
                              </a>
                              <button
                                role="menuitem"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLogoutMMIOnly();
                                  setAcctMenuOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  color: 'inherit'
                                }}
                              >
                                Logout - MMI Only
                              </button>
                              <button
                                role="menuitem"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLogoutAllSites();
                                  setAcctMenuOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  color: 'inherit'
                                }}
                              >
                                Logout - All Connected Sites
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <a
                              href={`${Membership}/login?returnTo=mmi`}
                              className="tp-header-setting-toggle"
                              style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <i className="fa-regular fa-circle-user f_sz16 mr-5" />
                              Sign In
                            </a>
                            <a
                              href={`${Membership}/signup`}
                              className="tp-header-setting-toggle"
                              style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Sign Up
                            </a>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* header bottom start */}
          <div id="header-sticky" className="tp-header-bottom-2 tp-header-sticky">
            <div className="container">
              <div className="tp-mega-menu-wrapper p-relative">
                <div className="row align-items-center">
                  <div className="col-xl-2 col-lg-3 col-md-5 col-sm-4 col-6 order-1 order-lg-1 order-xl-1">
                    <div className="logo">
                      <a href="/">
                        <img src="/assets/img/logo/logo.png" alt="logo" />
                      </a>
                    </div>
                  </div>
                  {showSearchSection && (
                    <div className="col-xl-5 col-lg-6 col-md-12 col-sm-12 col-12 order-3 order-lg-2 order-xl-2">
                      <div className="d-flex gap-2 mt-15 mt-lg-0">
                        <div className="col-4">
                          <div className="tp-shop-top-filter w-100 ms-0">
                            <button
                              type="button"
                              className="tp-filter-btn filter-open-btn tp-btn-blue1 w-100 tp_categorybtn_pdng_sm"
                              onClick={toggleCategoriesPanel}
                            >
                              <span>
                                <i className="fa-solid fa-sliders mr-5" />
                              </span>
                              Categories
                            </button>
                          </div>
                        </div>
                        <div className="col-8">
                          <div className="tp-header-search-2 mt-15 mt-sm-0">
                            <form onSubmit={handleSearchSubmit}>
                              <div>
                                <input
                                  type="text"
                                  className="search_frmcntrl"
                                  placeholder="Search for Brands or Products"
                                  value={searchInput}
                                  onChange={(e) => setSearchInput(e.target.value)}
                                  onFocus={() => {
                                    if (searchInput.trim().length >= 2 && (searchSuggestions.brands.length > 0 || searchSuggestions.categories.length > 0)) {
                                      setShowSuggestions(true);
                                    }
                                  }}
                                />
                                <button 
                                  type="submit"
                                  disabled={searchInput.trim().length < 2}
                                  style={{
                                    opacity: searchInput.trim().length < 2 ? 0.5 : 1,
                                    cursor: searchInput.trim().length < 2 ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  <i className="fa-solid fa-magnifying-glass" />
                                </button>
                              </div>
                            </form>
                            <div
                              ref={suggestionRef}
                              className="search_suggestion scrollbar_style"
                              style={{ display: showSuggestions ? 'block' : 'none' }}
                            >
                              {searchSuggestions.brands.length > 0 && (
                                <>
                                  <h6 className="f_sz15 f_themered">Brands</h6>
                                  <ul>
                                    {searchSuggestions.brands.map((brand) => (
                                      <li key={brand.id}>
                                        <a
                                          onClick={() => handleSuggestionClick('brand', brand)}
                                        >
                                          {brand.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                              {searchSuggestions.categories.length > 0 && (
                                <>
                                  <h6 className="f_sz15 f_themered mt-5">Categories</h6>
                                  <ul>
                                    {searchSuggestions.categories.map((category) => (
                                      <li key={category.id}>
                                        <a
                                          onClick={() => handleSuggestionClick('category', category)}
                                        >
                                          {category.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`col-xl-5 d-none d-xl-block order-xl-3 ${!showSearchSection ? 'offset-xl-5' : ''}`}>
                    <div className="main-menu menustyle_new menu-style-2">
                      <nav className="tp-main-menu-content">
                        <ul>
                          <li>
                            <Link to="/" className="active">Insider</Link>
                          </li>
                          <li className="has-dropdown">
                            <a>MyMilitary Brands</a>
                            <ul className="tp-submenu">
                              <li><a href={mymilitary} target="_blank" rel="noreferrer">MyMilitarySavings</a></li>
                              <li><a href={mymilitarylifestyle} target="_blank" rel="noreferrer">MyMilitaryLifestyle</a></li>
                              <li><a href={circleone || "https://www.mycircleone.com/"} target="_blank" rel="noreferrer">CircleOne</a></li>
                            </ul>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                  <div className="col-xl-5 col-lg-3 col-md-7 col-sm-8 col-6 d-xl-none order-2 order-lg-3">
                    <div className="tp-header-bottom-right d-flex align-items-center justify-content-end pl-30">
                      <div className="tp-header-action d-flex align-items-center ml-30">
                        <div className="tp-header-action-item tp-header-hamburger mr-20 d-xl-none">
                          <button
                            type="button"
                            className="tp-header-action-btn tp-offcanvas-open-btn"
                            onClick={toggleSidebar}
                          >
                            <i className="fa-solid fa-bars-staggered f_white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* sidepanel UI */}
      <div className="tp-filter-offcanvas-area">
        <div className="tp-filter-offcanvas-wrapper">
          <div className="cartmini__top p-relative">
            <div className="cartmini__top-title">
              <h4>Categories</h4>
            </div>
            <div className="cartmini__close">
              <button
                type="button"
                className="categories_closebtn filter-close-btn"
                onClick={closeCategoriesPanel}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
          <div className="tp-shop-sidebar">
            <div className="top_products_filtertype p-relative">
              <div>
                <div className="tp-shop-widget-categories tp_categories">
                  <div className="tp-shop-widget-checkbox tp_filter_categories">
                    <ul className="filter-items d-flex flex-wrap">
                      <li className="filter-item">
                        <div
                          className="crsr_pntr"
                          onClick={() => {
                            navigate('/product', { state: { filterLabel: 'Categories' } });
                            closeCategoriesPanel();
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="tp-shop-widget-brand-item f_helveticabold">All</div>
                        </div>
                      </li>
                      {renderCategoryTree(categories)}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default Header;
